// Which barcode reader a phone uses, shared by the ops app and the partner link. Chrome on
// Android has BarcodeDetector built in and reads Code128 natively. Anything else loads a small
// WebAssembly reader (the `barcode-detector` polyfill over ZXing) from the app's own files, so it
// also works with no signal once the app shell is cached. The polyfill is a separate chunk: phones
// with the built-in reader never download it.
//
// This file is NOT in the package index, so apps that never scan do not bundle it. Import it as
// `@ecobox/ui/detector`. Each app passes the URL of its own copy of the `.wasm` file:
//
//   import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';
//   export const { loadBarcodeDetector, warmUpScanner } = createDetectorKit({ wasmUrl });
import {
  BOX_BARCODE_FORMATS,
  loadNativeDetector,
  type BarcodeDetectorLike,
  type DetectorLoader,
} from './scanner';

export interface DetectorKitOptions {
  /** Where this app serves `zxing_reader.wasm` from (the `?url` import). Never a CDN. */
  wasmUrl: string;
}

export interface DetectorKit {
  /** The WebAssembly reader, built once. */
  loadPolyfillDetector(): Promise<BarcodeDetectorLike>;
  /** The built-in reader if it can read Code128, otherwise the polyfill. */
  loadBarcodeDetector: DetectorLoader;
  /**
   * Phones without a built-in Code128 reader need the polyfill and its `.wasm` file. Fetching
   * them once while online puts them in the app's offline cache, so the camera also works the
   * first time it is used with no signal. Phones with the built-in reader download nothing.
   */
  warmUpScanner(): Promise<void>;
}

export function createDetectorKit({ wasmUrl }: DetectorKitOptions): DetectorKit {
  let polyfill: Promise<BarcodeDetectorLike> | undefined;

  const loadPolyfillDetector = (): Promise<BarcodeDetectorLike> => {
    polyfill ??= (async () => {
      const lib = await import('barcode-detector/ponyfill');
      lib.setZXingModuleOverrides({
        locateFile: (path: string, prefix: string) =>
          path.endsWith('.wasm') ? wasmUrl : `${prefix}${path}`,
      });
      return new lib.BarcodeDetector({ formats: ['code_128'] });
    })().catch((err: unknown) => {
      polyfill = undefined;
      throw err;
    });
    return polyfill;
  };

  const loadBarcodeDetector: DetectorLoader = async () =>
    (await loadNativeDetector(BOX_BARCODE_FORMATS)) ?? (await loadPolyfillDetector());

  const warmUpScanner = async (): Promise<void> => {
    try {
      if (await loadNativeDetector(BOX_BARCODE_FORMATS)) return;
      await import('barcode-detector/ponyfill');
      await fetch(wasmUrl);
    } catch (err) {
      console.warn('The barcode reader could not be prepared for offline use.', err);
    }
  };

  return { loadPolyfillDetector, loadBarcodeDetector, warmUpScanner };
}
